import { useState, useEffect, useRef, useMemo } from 'react';
import { DialogContent, DialogTitle, Grid, useTheme, useMediaQuery, Button, Typography } from '@mui/material';
import { RoundedDialog, StandardDialogActions } from '../Components';
import { cloneDeep, isArray, isEmpty, isEqual, isFunction, isNumber } from 'lodash';
import { Field } from '.';
import { DeleteOutlineRounded, ArrowBackIosRounded, ArrowForwardIosRounded, AddRounded } from '@mui/icons-material';
import StateManager from '../StateManager';
import { v4 } from 'uuid';

export default function RowDialog({
  open,
  onClose,
  initialRows,
  currRowId,
  columns,
  editable,
  onChange,
  activityInfo,
  allCreatedActions,
  onCreatedAction,
  onDeletedAction,
  addRowButtonText = 'add another row',
  onRowSelect,
  tableConfig,
}) {
  const timer = useRef(null);
  const firstRender = useRef(true);
  const SAVING_INTERVAL = 1200;
  const [rows, setRows] = useState([]);
  const [currRowIndex, setCurrRowIndex] = useState(-1);
  const [items, setItems] = useState([]);
  const theme = useTheme();
  const largeDevices = useMediaQuery(theme.breakpoints.up('sm'));
  const allowEditPreBuiltRows = tableConfig?.settings?.allowEditPreBuiltRows;

  const requiredColumns = isArray(tableConfig?.settings?.requiredColumns) ? tableConfig?.settings?.requiredColumns : [];
  const calculationColumnsIds = items
    .filter((x) => x.fieldType === 'calculation' || x.fieldType === 'status')
    .map((x) => x.id);

  function activateTimer(value) {
    clearTimer();
    timer.current = setTimeout(() => onChange(value), SAVING_INTERVAL);
  }

  function clearTimer() {
    if (timer.current != null) {
      clearTimeout(timer.current);
    }
  }

  function onSave(column, fieldId, value) {
    // for data sets and color check fields the logic is slightly different
    const columnValue =
      column.fieldType === 'dataSet'
        ? { rows: value.value?.rows, value: value.value?.rows?.map((x) => x.id) }
        : column.fieldType === 'colorCheck'
        ? value.value
        : value;
    rows[currRowIndex][fieldId] = { ...columnValue, filled: true };
    activateTimer(rows);
  }

  useEffect(() => {
    if (!isArray(initialRows)) return;
    if (isEqual(rows, initialRows)) return;

    // first render - update all
    if (firstRender.current) {
      firstRender.current = false;
      setRows(cloneDeep(initialRows));
    } else {
      let updated = false;
      const copy = [...rows];
      // otherwise rows could be updated - find those updated
      for (const [index, row] of initialRows.entries()) {
        const existingIndex = copy.findIndex((x) => x.id === row.id);

        // might have been deleted
        if (existingIndex === -1) {
          // means the row has been added
          if (index === initialRows.length - 1) {
            copy.push(row);
            updated = true;
          }

          continue;
        }

        // find out what's been updated - only update calculation and status fields
        for (const columnId of calculationColumnsIds) {
          if (!isEqual(copy[existingIndex][columnId], row[columnId])) {
            copy[existingIndex][columnId] = row[columnId];
            updated = true;
          }
        }
      }

      if (updated) {
        setRows(copy);
      }
    }
  }, [initialRows]); // eslint-disable-line

  useEffect(() => {
    if (!open || !isArray(rows)) return;
    const index = rows.findIndex((x) => x.id === currRowId);
    if (index === -1) return;
    setCurrRowIndex(index);
    // don't need to update index when rows updated
  }, [open, currRowId, rows]); // eslint-disable-line

  useEffect(() => {
    if (!open || !isArray(columns)) {
      setItems([]);
      return;
    }
    setItems(cloneDeep(columns));
  }, [open, columns]);

  const { currRowEditable } = useMemo(() => {
    if (!isArray(rows) || !(currRowIndex > -1)) return { currRowEditable: null };

    const row = rows[currRowIndex];
    // row editable if it's not pre-built or editing pre-built rows is allowed
    const rowEditable = editable && (!row?.initial || allowEditPreBuiltRows);

    return { currRowEditable: rowEditable };
  }, [rows, currRowIndex, allowEditPreBuiltRows, editable]);

  function confirmDelete() {
    StateManager.setConfirm('You are about to delete this row', deleteRow);
  }

  function swipeToRow(index) {
    setCurrRowIndex(index);
  }

  const displayPrev = currRowIndex > 0 && rows.length > 1;
  const displayNext = currRowIndex < rows.length - 1 && rows.length > 1;

  function deleteRow() {
    if (displayPrev) {
      swipeToRow(currRowIndex - 1);
    } else if (displayNext) {
      swipeToRow(currRowIndex + 1);
    }

    const result = rows.filter((_x, i) => i !== currRowIndex);
    onChange(result);
    setRows(result);

    if (rows.length === 1) {
      onClose();
    }
  }

  function defaultRow() {
    return {
      id: v4(),
      index: rows.length + 1,
      ...columns.reduce((prev, curr) => ({ ...prev, [curr.id]: { value: null } }), {}),
    };
  }

  function addRow() {
    const newRow = defaultRow();
    const result = [...rows, newRow];
    clearTimer(); // important, otherwise 2 results can be sent
    setRows(result);
    onChange(result);
    onRowSelect(newRow.id);
  }

  function isCellEditable(column, row) {
    // column value is set with automations
    if (column.setAutomatically) return false;
    // row added by the user or editing pre-built rows allowed
    if (!row?.initial || allowEditPreBuiltRows) return true;
    // statuses are always editable
    if (column.fieldType === 'status') return true;
    // value not provided - even initial rows are editable
    const cellData = row[column.id];
    if (isEmpty(cellData?.value) && !isNumber(cellData?.value)) return true;

    // filled by user - editable even in pre-built rows
    if (cellData?.filled === true) return true;

    // by default false lol
    return false;
  }

  return (
    <RoundedDialog
      open={open}
      onClose={onClose}
      maxWidth={'md'}
      fullWidth
      className="scroll-bar"
      fullScreen={!largeDevices}
    >
      <DialogTitle>
        <Grid container justifyContent={'space-between'} alignItems="center">
          <Button
            disabled={!displayPrev}
            startIcon={<ArrowBackIosRounded />}
            onClick={() => swipeToRow(currRowIndex - 1)}
          >
            prev
          </Button>

          {!isEmpty(rows) && (
            <Typography variant="h6">
              #{currRowIndex + 1}/{rows.length}
            </Typography>
          )}

          <Button
            disabled={!displayNext}
            endIcon={<ArrowForwardIosRounded />}
            onClick={() => swipeToRow(currRowIndex + 1)}
          >
            next
          </Button>
        </Grid>
      </DialogTitle>
      <DialogContent>
        <Grid container>
          {isEmpty(rows) && <Typography variant="h6">No rows available</Typography>}
          {isArray(rows) &&
            rows.map((row, index) => {
              // .map is to avoid bugs with the fields preserve their values when switching between rows
              if (index !== currRowIndex) return null;

              return (
                <Grid
                  container
                  sx={{ height: largeDevices ? '70vh' : '', overflow: 'auto' }}
                  alignContent={'flex-start'}
                  key={index}
                >
                  {items.map((column) => (
                    <Field
                      key={column.id}
                      item={{
                        ...column,
                        filledValue: row[column.id],
                        required: requiredColumns.includes(column.id),
                      }}
                      editable={editable && isCellEditable(column, row)}
                      onSave={(fieldId, value) => onSave(column, fieldId, value)}
                      activityInfo={activityInfo}
                      allCreatedActions={
                        isArray(allCreatedActions)
                          ? allCreatedActions.filter((x) => x.columnId === column.id && x.rowId === row.id)
                          : null
                      }
                      onCreatedAction={
                        isFunction(onCreatedAction)
                          ? (action) => onCreatedAction({ ...action, columnId: column.id, rowId: row.id })
                          : null
                      }
                      onDeletedAction={onDeletedAction}
                      cellInfo={{ columnId: column.id, rowId: row.id }}
                    />
                  ))}
                </Grid>
              );
            })}
        </Grid>
      </DialogContent>
      <StandardDialogActions
        additionalActions={
          editable &&
          currRowEditable && (
            <Button
              startIcon={<DeleteOutlineRounded />}
              color="secondary"
              onClick={confirmDelete}
              style={{ marginRight: 'auto' }}
            >
              delete
            </Button>
          )
        }
        onClose={onClose}
        onDone={addRow}
        hideDone={!editable}
        doneButtonIcon={<AddRounded />}
        doneButtonText={addRowButtonText}
      />
    </RoundedDialog>
  );
}
